import { rootMargin } from "./rootMargin";

type PositionObserverCallback = (
  {
    prevClientRect,
    clientRect,
  }: {
    prevClientRect: DOMRectReadOnly;
    clientRect: DOMRectReadOnly;
  },
  entries: IntersectionObserverEntry[],
  observer: PositionObserver,
) => void;

/*                                                   */
/*                                  */
const threshold = [...Array.from({ length: 1000 }, (_, i) => i / 1000), 1];

export class PositionObserver {
  target: Element;
  callback: PositionObserverCallback;
  observer: IntersectionObserver | null = null;

  constructor(target: Element, callback: PositionObserverCallback) {
    this.target = target;
    this.callback = callback;
    this.observe();
  }

  public disconnect(): void {
    this.unobserve();
  }

  protected observe(): void {
    this.observer = this.createIntersectionObserver();
    this.observer.observe(this.target);
  }
  protected unobserve(): void {
    if (!this.observer) return;
    this.observer.unobserve(this.target);
    this.observer.disconnect();
    this.observer = null;
  }

  protected handleIntersection = (
    entries: IntersectionObserverEntry[],
    prevClientRect: DOMRect,
  ): void => {
    const clientRect = this.target.getBoundingClientRect();
    if (
      clientRect.top === prevClientRect.top &&
      clientRect.left === prevClientRect.left &&
      clientRect.width === prevClientRect.width &&
      clientRect.height === prevClientRect.height
    ) {
      return;
    }
    this.unobserve();
    this.observe();
    this.callback(
      {
        prevClientRect,
        clientRect,
      },
      entries,
      this,
    );
  };

  protected createIntersectionObserver(): IntersectionObserver {
    const clientRect = this.target.getBoundingClientRect();
    const root = document.documentElement;
    const rootRect = root.getBoundingClientRect();
    const observer = new IntersectionObserver(
      (entries) => this.handleIntersection(entries, clientRect),
      {
        root: document.documentElement,
        rootMargin: rootMargin(clientRect, rootRect),
        threshold,
      },
    );
    return observer;
  }
}
